import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { PageEvent } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  ActivityDateRange,
  ActivityDateRangeComponent,
} from '@app/pages/devices/components/activity-date-range/activity-date-range.component';
import { ActivityMessageDialogComponent } from '@app/pages/devices/components/activity-message-dialog/activity-message-dialog.component';
import { FILTERABLE_ACTIONS } from '@app/pages/devices/components/device-activity-tab/device-activity-tab.component';
import {
  ACTIVITY_STATUSES,
  activityStatusLabel,
  deviceActionLabel,
} from '@core/helpers/activity-label.helper';
import { Activity, ActivityFilters } from '@core/models/activity.model';
import { TableColumnConfig, TableColumnType } from '@core/models/skeleton.model';
import { ActivityService } from '@core/services/activity.service';
import { TableComponent } from '@custom-components/general/table/table.component';
import {
  FilterGroup,
  TableFilterBarComponent,
} from '@custom-components/general/table-filter-bar/table-filter-bar.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { InstallationDataSourcesStore } from '@root/app/store/installation-data-sources.store';
import { catchError, of } from 'rxjs';
import { FedDevice } from '../../fed-devices';
import {
  ActivityRead,
  activityRow,
  INSTALLATION_ACTIONS,
  installationActionLabel,
} from '../../installation-activity';
import { InstallationActivityEmptyComponent } from '../installation-activity-empty/installation-activity-empty.component';

const PAGE_SIZE = 10;

const ONLY_INSTALLATION = 'onlyInstallation';

@Component({
  selector: 'xemex-installation-activity-tab',
  imports: [
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    ActivityDateRangeComponent,
    InstallationActivityEmptyComponent,
    TableComponent,
    TableFilterBarComponent,
    TranslateModule,
  ],
  providers: [DatePipe, InstallationDataSourcesStore],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './installation-activity-tab.component.html',
})
export class InstallationActivityTabComponent {
  readonly installationOid = input.required<string>();
  // The devices that feed the installation, which the empty state points to.
  readonly devices = input<FedDevice[]>([]);

  private readonly activityService = inject(ActivityService);
  private readonly dataSources = inject(InstallationDataSourcesStore);
  private readonly dialog = inject(MatDialog);
  private readonly datePipe = inject(DatePipe);
  private readonly translate = inject(TranslateService);

  readonly pageSize = PAGE_SIZE;

  readonly columns: TableColumnConfig[] = [
    {
      field: 'source',
      label: 'installations.activity.columns.source',
      width: '16%',
      type: TableColumnType.Link,
      linkFn: row => row['sourceLink'] as unknown[] | null,
    },
    {
      field: 'action',
      label: 'installations.activity.columns.action',
      width: '16%',
      bold: true,
    },
    { field: 'message', label: 'installations.activity.columns.message', width: '28%' },
    {
      field: 'status',
      label: 'installations.activity.columns.status',
      width: '10%',
      type: TableColumnType.Tag,
      stateField: 'statusState',
    },
    { field: 'initiatedBy', label: 'installations.activity.columns.initiatedBy', width: '11%' },
    { field: 'initiatedOn', label: 'installations.activity.columns.initiatedOn', width: '11%' },
  ];

  // The filter bar holds its choices as the labels it shows, so each label is mapped back to the
  // value the route filters on.
  private readonly actionLabelToKey = new Map<string, string>([
    ...INSTALLATION_ACTIONS.map(
      key => [installationActionLabel(this.translate, key), key] as const,
    ),
    ...FILTERABLE_ACTIONS.map(key => [deviceActionLabel(this.translate, key), key] as const),
  ]);
  private readonly statusLabelToKey = new Map<string, string>(
    ACTIVITY_STATUSES.map(status => [activityStatusLabel(this.translate, status), status]),
  );

  readonly filterGroups: FilterGroup[] = [
    {
      key: ONLY_INSTALLATION,
      label: this.translate.instant('installations.activity.onlyInstallation'),
      type: 'toggle',
    },
    {
      key: 'action',
      label: this.translate.instant('devices.detailTabs.activity.filter.action'),
      options: [...this.actionLabelToKey.keys()],
    },
    {
      key: 'status',
      label: this.translate.instant('devices.detailTabs.activity.filter.status'),
      options: [...this.statusLabelToKey.keys()],
    },
  ];

  private readonly filters = signal<ActivityFilters>({});
  private readonly onlyInstallation = signal(false);
  private readonly pageIndex = signal(0);

  private readonly activityRead = rxResource({
    params: () => ({
      installationOid: this.installationOid(),
      onlyInstallation: this.onlyInstallation() || undefined,
      ...this.filters(),
      limit: PAGE_SIZE,
      offset: this.pageIndex(),
    }),
    stream: ({ params }) =>
      this.activityService.getActivities(params).pipe(catchError(() => of<ActivityRead>('failed'))),
  });

  private readonly response = computed(() => {
    const read = this.activityRead.value();
    return read === 'failed' ? undefined : read;
  });

  readonly loading = computed(() => this.activityRead.isLoading());
  readonly failed = computed(() => this.activityRead.value() === 'failed');
  readonly totalCount = computed(() => this.response()?.numberOfItems ?? 0);
  readonly filtered = computed(
    () => this.onlyInstallation() || Object.values(this.filters()).some(Boolean),
  );

  readonly tableData = computed<Record<string, unknown>[]>(() => {
    const devices = this.dataSources.dataSources()?.devices ?? [];
    return (this.response()?.activities ?? []).map(activity => ({
      ...activityRow(this.translate, activity, devices),
      initiatedBy: activity.initiated_by,
      initiatedOn: this.datePipe.transform(activity.initiated_on, 'd MMM yyyy, HH:mm') ?? '',
      activity,
    }));
  });

  constructor() {
    // The data sources hold the type of each device at the location, which names a device's rows.
    effect(() => {
      const installationOid = this.installationOid();
      untracked(() => this.dataSources.load(installationOid));
    });
  }

  onSearchChange(query: string): void {
    this.applyFilters({ ...this.filters(), initiatedBy: query || undefined });
  }

  onFilterChange(filters: Record<string, string[]>): void {
    const actions = (filters['action'] ?? []).map(
      label => this.actionLabelToKey.get(label) ?? label,
    );
    const statuses = (filters['status'] ?? []).map(
      label => this.statusLabelToKey.get(label) ?? label,
    );
    this.onlyInstallation.set((filters[ONLY_INSTALLATION] ?? []).length > 0);
    this.applyFilters({
      ...this.filters(),
      action: actions.length ? actions.join(',') : undefined,
      status: statuses.length ? statuses.join(',') : undefined,
    });
  }

  onDateRangeChange(range: ActivityDateRange): void {
    const { initiatedOnFrom: _from, initiatedOnTo: _to, ...rest } = this.filters();
    this.applyFilters({ ...rest, ...range });
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex.set(event.pageIndex);
  }

  openMessage(activity: Activity): void {
    this.dialog.open(ActivityMessageDialogComponent, { data: { activity }, autoFocus: false });
  }

  private applyFilters(filters: ActivityFilters): void {
    this.pageIndex.set(0);
    this.filters.set(filters);
  }
}
